import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["edges", "defaultNodeHeights", "nodeWidth", "nodeHeight"];
import { createImmutableObjectClass } from 'immutable-less/ImmutableObject';
import { uniqueIdForObject } from 'immutable-less/utils/uniqueIdForObject';
import difference from 'hs-lodash/difference';
import memoize from 'transmute/memoize';
import Edge from 'automation-ui-directed-graph/Edge';
import { CARD_WIDTH, DEFAULT_CARD_HEIGHT } from 'automation-ui-flow-core/canvas/graphConstants';
import calculateNodePositions from 'automation-ui-directed-graph/utils/calculateNodePositions';
export const calculateAdjacencyMap = memoize(edges => {
  let adjacencyMap = new Map();
  edges.forEach(edge => {
    const fromNode = edge.getFromNode();
    const adjacentEdges = adjacencyMap.get(fromNode) || [];
    adjacencyMap = adjacencyMap.set(fromNode, [...adjacentEdges, edge]);
  });
  return adjacencyMap;
}, edges => `${uniqueIdForObject(edges)}`);

/**
 * Create a reverse edge map from "toNode"s to arrays
 * of "fromNode"s. By default, go to edges are
 * considered termination conditions.
 */
export const calculateReverseAdjacencyMap = memoize((edges, includeGoToEdges = false) => {
  const leafEdgesToNodes = [];
  let reverseAdjacencyMap = new Map();
  edges.forEach(edge => {
    const toNode = edge.getToNode();
    if (!toNode || !includeGoToEdges && edge.isGoToEdge()) {
      leafEdgesToNodes.push(edge);
    } else {
      const adjacentEdges = reverseAdjacencyMap.get(toNode) || [];
      reverseAdjacencyMap = reverseAdjacencyMap.set(toNode, [...adjacentEdges, edge]);
    }
  });
  return {
    leafEdgesToNodes,
    reverseAdjacencyMap,
    // TODO: remove `reverseAdjancencyMap` when all usage of it has been removed from external sources
    reverseAdjancencyMap: reverseAdjacencyMap
  };
}, (edges, includeGoToEdges) => `${uniqueIdForObject(edges)}-${includeGoToEdges}`);
export const getLeafIds = memoize(edges => edges.filter(edge => edge.getToNode() === null || edge.getToNode() === undefined).map(edge => edge.getId()), edges => `${uniqueIdForObject(edges)}`);
export const getDescendentEdges = (startingNode, edges, followGoToEdges = false) => {
  const adjacencyMap = calculateAdjacencyMap(edges);
  let descendentEdges = adjacencyMap.get(startingNode) || [];
  let edgesToFollow = [...descendentEdges];
  let edgesFollowed = new Set();
  while (edgesToFollow.length > 0) {
    const edge = edgesToFollow.pop();
    const toNode = edge.getToNode();

    // Skip this edge if it has already been followed, otherwise enforce goto restriction
    if (edgesFollowed.has(edge) || !followGoToEdges && edge.isGoToEdge()) {
      continue;
    }
    const reachableEdges = adjacencyMap.get(toNode) || [];
    descendentEdges = descendentEdges.concat(reachableEdges);
    edgesToFollow = edgesToFollow.concat(reachableEdges);
    edgesFollowed = edgesFollowed.add(edge);
  }
  return descendentEdges;
};
export const getAncestorEdges = memoize((startingNode, edges, followGoToEdges = false) => {
  const {
    reverseAdjacencyMap
  } = calculateReverseAdjacencyMap(edges, followGoToEdges);
  let ancestorEdges = startingNode !== null ? reverseAdjacencyMap.get(startingNode) || [] : [];
  let edgesToFollow = [...ancestorEdges];
  let edgesFollowed = new Set();
  while (edgesToFollow.length > 0) {
    const edge = edgesToFollow.pop();
    const fromNode = edge.getFromNode();

    // Skip this edge if it has already been followed, otherwise enforce goto restriction
    if (edgesFollowed.has(edge) || !followGoToEdges && edge.isGoToEdge()) {
      continue;
    }
    const reachableEdges = reverseAdjacencyMap.get(fromNode) || [];
    ancestorEdges = ancestorEdges.concat(reachableEdges);
    edgesToFollow = edgesToFollow.concat(reachableEdges);
    edgesFollowed = edgesFollowed.add(edge);
  }
  return ancestorEdges;
}, (startingNode, edges, followGoToEdges) => `${startingNode}-${uniqueIdForObject(edges)}-${followGoToEdges}`);
/**
 * This is an abstract graph representation of a
 * DAG, used primarily to draw a "tree like" diagram
 * that is capable of creating "go to" edges while
 * maintaining stable rendering. That is, the
 * classification of certain edges as "go to" edges
 * allows the DAG to be rendered as though it were
 * a tree without the need to recalculate node
 * positions.
 *
 * The optimistic node positioning method is also
 * powerful when used to develop the virtual rendering
 * features of the tree component.
 *
 * Note that this record is meant to be as dry as
 * possible and should only ever be describing the
 * graph's connectivity and node/edge positioning.
 *
 * IMPORTANT: for identification purposes, nodes of
 * type "go to" must have `goToAction` in their
 * nodeIds, for now.
 */
export default class DirectedNodes extends createImmutableObjectClass({
  startingNodeId: null,
  nodeIds: [],
  edges: [],
  goToPaths: {},
  defaultNodeHeights: new Map(),
  nodeWidth: CARD_WIDTH,
  nodeHeight: DEFAULT_CARD_HEIGHT
}, {
  fromJSON: _ref => {
    let {
        edges,
        defaultNodeHeights,
        nodeWidth = CARD_WIDTH,
        nodeHeight = DEFAULT_CARD_HEIGHT
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    const edgeObjects = edges.map(edge => new Edge(edge));
    return Object.assign({}, rest, {
      nodeWidth,
      nodeHeight,
      edges: edgeObjects,
      defaultNodeHeights
    });
  },
  toJSON: record => record
}) {
  getEdges() {
    return this.edges;
  }
  getStartingNodeId() {
    return this.startingNodeId;
  }
  getNodeIds() {
    return this.nodeIds;
  }
  getDefaultNodeHeight(nodeId) {
    var _this$defaultNodeHeig;
    return (_this$defaultNodeHeig = this.defaultNodeHeights.get(nodeId)) !== null && _this$defaultNodeHeig !== void 0 ? _this$defaultNodeHeig : this.nodeHeight;
  }
  getGoToPaths() {
    return this.goToPaths;
  }
  getFollowingEdges(node) {
    return this.getEdges().filter(edge => edge.getFromNode() === node);
  }

  /**
   * Optimistic algorithm that uses current observed heights
   * of nodes to calculate the absolute locations of nodes.
   * Node coordinates are the center of the node.
   * This function will always have exact horizontal coords
   * but will only have estimates for the heights until
   * those nodes are rendered to the page. This is because
   * heights are dynamic.
   *
   * The recursion logic leads to a DFS starting at
   * the startingNodeId and ending at each of the
   * leaf nodes.
   * @deprecated Use automation-ui-directed-graph/utils/calculateNodePositions
   * @param {Map<NodeId, number>} nodeHeights Keyed off by node id, contains
   *                          heights in pixels
   */
  calculateNodePositions(nodeHeights) {
    return calculateNodePositions(this, nodeHeights);
  }

  /**
   * Given a node id, find the root loop node that the action
   * is in the loop for. If the action is not in a loop,
   * returns null. There should only ever be one edge
   * marked with the loop boolean because (for now) loops
   * cannot be nested, therefore no one reversed execution
   * path should see more than 1 loop node success edge.
   */
  getAncestorRootLoopNode(nodeId) {
    const ancestorEdges = getAncestorEdges(nodeId, this.getEdges());
    const loopEdge = ancestorEdges.find(edge => edge.isLoopEdge);
    return loopEdge ? loopEdge.getFromNode() : null;
  }

  /**
   * For a list of nodes, return a list of those that are in
   * a loop context. This is helpful for determining nodes
   * that should never be allowed as the target of a go to
   * action. Nodes in the same loop context as the goalNodeId
   * are ignored
   */
  getNodesInLoopContext(nodes, goalNodeId) {
    const goalAncestorRootLoopNode = this.getAncestorRootLoopNode(goalNodeId);
    const nodesInLoopContext = [];
    nodes.forEach(nodeId => {
      const ancestorRootLoopNodeForNodeId = this.getAncestorRootLoopNode(nodeId);
      if (ancestorRootLoopNodeForNodeId && ancestorRootLoopNodeForNodeId !== goalAncestorRootLoopNode) {
        nodesInLoopContext.push(nodeId);
      }
    });
    return nodesInLoopContext;
  }

  /**
   * Enumerates valid target nodes for a goto edge following a given parent node
   * IMPORTANT: must be used on a graph whose goto edges are rendered as actions
   *
   * @param goalNodeIdRaw the ID of the goto edge's parent node
   * @returns an array of node IDs (as strings) representing valid target nodes
   *          of a goto edge for the given parent node
   */
  getNodeIdsForGoToNode(goalNodeIdRaw, edgeId) {
    const goalNodeId = Number(goalNodeIdRaw) || goalNodeIdRaw;
    const edges = this.getEdges();
    const ancestorEdges = getAncestorEdges(goalNodeId, edges, true);
    const nodeIdsWithPathToGoalNode = ancestorEdges.map(edge => edge.getFromNode()).concat([goalNodeId]);
    let nodeIdsForGoToNode = difference(this.getNodeIds(), nodeIdsWithPathToGoalNode);
    const selfGoToEdge = edges.find(edge => edge.edgeId === edgeId);
    let ancestorRootLoopNode = this.getAncestorRootLoopNode(goalNodeId);
    const nodesInLoopContext = this.getNodesInLoopContext(nodeIdsForGoToNode, goalNodeId);
    nodeIdsForGoToNode = difference(nodeIdsForGoToNode, nodesInLoopContext);
    if (selfGoToEdge && selfGoToEdge.isLoopEdge) {
      ancestorRootLoopNode = goalNodeId;
    }
    if (ancestorRootLoopNode) {
      nodeIdsForGoToNode = nodeIdsForGoToNode.concat(ancestorRootLoopNode);
    }
    return nodeIdsForGoToNode;
  }
  getNodeIdsForDataSourceNode(goalNodeIdRaw) {
    // Goal node == parent of new node
    const goalNodeId = Number(goalNodeIdRaw) || goalNodeIdRaw;

    // Build Map for each node to the goal node
    const ancestorEdges = getAncestorEdges(goalNodeId, this.getEdges(), true);
    const nodeIdsWithPathToGoalNode = ancestorEdges.map(edge => edge.getFromNode()).concat([goalNodeId]);
    return nodeIdsWithPathToGoalNode;
  }
}